import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { fetchThread, createPost, createThread, fetchThreadsOrganized, createThreadReport, createPostReport, type ThreadWithPosts, type Post, type Thread, type TopicThreadsResponse } from '../../features/topics/api';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { AdBanner } from '../../components/ads/AdBanner';
import { MessageSquare, Clock, Flag, ArrowLeft, Eye, MessageCircle, CornerDownRight, X } from 'lucide-react';
import { getStoredUserInfo } from '../../services/authService';
import { getCurrentAliases } from '../../services/aliasService';
import { ImageUpload } from '../../components/upload/ImageUpload';

export function ThreadPage() {
  const { threadId } = useParams<{ threadId: string }>();
  const [threadData, setThreadData] = useState<ThreadWithPosts | null>(null);
  const [childThreads, setChildThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyToPost, setReplyToPost] = useState<Post | null>(null);
  const [showCreateThreadForm, setShowCreateThreadForm] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ type: 'thread' | 'post'; id: string } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [jumpToPage, setJumpToPage] = useState('');
  const postsPerPage = 10;
  const navigate = useNavigate();

  useEffect(() => {
    if (threadId) {
      loadThread();
    }
  }, [threadId]);

  async function loadThread() {
    if (!threadId) return;

    try {
      setLoading(true);
      const data = await fetchThread(threadId);
      setThreadData(data);
      setError(null);

      // If this is a subthread, fetch child threads
      if (data.thread.isSubthread && data.thread.topicId) {
        const organizedData = await fetchThreadsOrganized(data.thread.topicId, 100);
        const children = organizedData.subthreads
          .find(st => st.subthread.id === threadId)
          ?.threads || [];
        setChildThreads(children);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load thread');
    } finally {
      setLoading(false);
    }
  }

  function handleJumpToPage() {
    const pageNum = parseInt(jumpToPage);
    const totalPages = Math.ceil((threadData?.posts.length || 0) / postsPerPage);
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      setJumpToPage('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 dark:border-brand-400"></div>
            <p className="mt-4 text-slate-600 dark:text-slate-400 font-medium">Loading thread...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !threadData) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-xl p-6 shadow-lg">
            <h3 className="text-lg font-bold text-red-900 dark:text-red-100 mb-2">Error Loading Thread</h3>
            <p className="text-red-700 dark:text-red-400 mb-4">{error || 'Thread not found'}</p>
            <Button onClick={loadThread} className="shadow-md">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const { thread, posts } = threadData;
  const totalPages = Math.ceil(posts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const endIndex = startIndex + postsPerPage;
  const paginatedPosts = posts.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-white/95 dark:bg-slate-800/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-700 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Button
                onClick={() => navigate(thread.topicSlug ? `/forum/topics/${thread.topicSlug}` : '/forum')}
                variant="secondary"
                size="sm"
                className="flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100 truncate">{thread.title}</h1>
                <div className="flex items-center gap-2 mt-1 text-xs text-slate-600 dark:text-slate-400 flex-wrap">
                  <span className="flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" />
                    {posts.length} {posts.length === 1 ? 'post' : 'posts'}
                  </span>
                  {thread.viewCount > 0 && (
                    <>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {thread.viewCount} views
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {thread.pinned && (
                <span className="px-2 py-1 bg-brand-100 dark:bg-brand-900/30 border border-brand-300 dark:border-brand-700 rounded text-xs font-medium text-brand-700 dark:text-brand-400">
                  Pinned
                </span>
              )}
              {thread.locked && (
                <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded text-xs font-medium text-yellow-700 dark:text-yellow-400">
                  Locked
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Thread Info Card with Original Post */}
        <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-850 border-2 border-slate-200 dark:border-slate-700 rounded-xl p-6 mb-6 shadow-md">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex-1">
              <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                Started by <span className="font-semibold text-slate-900 dark:text-slate-100">{thread.authorAlias}</span>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-500">
                {new Date(thread.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
            <button
              onClick={() => setReportTarget({ type: 'thread', id: thread.id })}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 hover:border-red-300 dark:hover:border-red-700 transition-all duration-200"
              title="Report this thread"
            >
              <Flag className="w-4 h-4" />
              <span className="hidden sm:inline">Report</span>
            </button>
          </div>

          {/* Original Post Content */}
          {posts.length > 0 && (() => {
            const originalPost = posts[posts.length - 1]; // Last post in DESC order is the original post
            return (
              <div className="pt-4 border-t border-slate-300 dark:border-slate-600">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-brand-600 border-2 border-brand-400 rounded-full flex items-center justify-center text-white font-bold shadow-sm">
                    {originalPost.authorAlias.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 dark:text-slate-100">{originalPost.authorAlias}</div>
                    <span className="inline-flex items-center px-2 py-0.5 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/40 dark:to-emerald-900/40 border border-green-300 dark:border-green-700 rounded-full text-xs font-bold text-green-800 dark:text-green-300">
                      ✨ Original Post
                    </span>
                  </div>
                </div>
                <div className="text-base leading-relaxed text-slate-800 dark:text-slate-200 whitespace-pre-wrap break-words bg-white dark:bg-slate-900/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                  {originalPost.body}
                </div>
                <AttachmentGallery attachments={originalPost.attachments} />
              </div>
            );
          })()}
        </div>

        {/* Ad Banner */}
        <AdBanner topicId={thread.topicId} type="inline" />

        {/* Posts */}
        <div className="space-y-4 mb-8">
          {paginatedPosts.map((post, index) => {
            const parentPost = post.parentPostId
              ? posts.find(p => p.id === post.parentPostId)
              : null;
            const actualIndex = startIndex + index;
            const postNumber = posts.length - actualIndex; // Reverse numbering since newest first
            const isOriginalPost = actualIndex === posts.length - 1; // Last post in DESC order is the original

            return (
              <PostCard
                key={post.id}
                post={post}
                postNumber={postNumber}
                isFirstPost={isOriginalPost}
                onReport={() => setReportTarget({ type: 'post', id: post.id })}
                onReply={() => {
                  setReplyToPost(post);
                  setShowReplyForm(false);
                }}
                parentPost={parentPost}
              />
            );
          })}
        </div>

        {/* Enhanced Pagination */}
        {totalPages > 1 && (
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6 shadow-sm mb-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setCurrentPage(p => Math.max(1, p - 1));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  disabled={currentPage === 1}
                >
                  ← Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setCurrentPage(p => Math.min(totalPages, p + 1));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </Button>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Page {currentPage} of {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={jumpToPage}
                    onChange={(e) => setJumpToPage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleJumpToPage()}
                    placeholder="Jump to..."
                    className="w-24 px-3 py-1.5 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                  <Button
                    size="sm"
                    onClick={handleJumpToPage}
                    disabled={!jumpToPage}
                  >
                    Go
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Report Modal */}
        {reportTarget && (
          <ReportModal
            targetType={reportTarget.type}
            targetId={reportTarget.id}
            onClose={() => setReportTarget(null)}
          />
        )}

        {/* Reply Button */}
        {!thread.locked && (
          <div className="mb-8">
            <Button onClick={() => setShowReplyForm(true)} className="w-full py-4 text-lg font-semibold shadow-md hover:shadow-lg transition-shadow">
              💬 Reply to Thread
            </Button>
          </div>
        )}

        {/* Reply Modal */}
        {showReplyForm && !replyToPost && (
          <Modal
            isOpen={showReplyForm}
            onClose={() => setShowReplyForm(false)}
            title="Post a Reply"
            size="xl"
          >
            <ReplyForm
              threadId={thread.id}
              onSuccess={() => {
                setShowReplyForm(false);
                loadThread();
              }}
              onCancel={() => setShowReplyForm(false)}
            />
          </Modal>
        )}

        {/* Reply to Post Modal */}
        {replyToPost && (
          <Modal
            isOpen={!!replyToPost}
            onClose={() => setReplyToPost(null)}
            title={`Reply to @${replyToPost.authorAlias}`}
            size="xl"
          >
            <ReplyForm
              threadId={thread.id}
              parentPostId={replyToPost.id}
              parentPost={{
                id: replyToPost.id,
                authorAlias: replyToPost.authorAlias,
                body: replyToPost.body,
              }}
              onSuccess={() => {
                setReplyToPost(null);
                loadThread();
              }}
              onCancel={() => setReplyToPost(null)}
            />
          </Modal>
        )}

        {thread.locked && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-800 rounded-xl p-6 text-center shadow-sm mb-8">
            <div className="text-4xl mb-2">🔒</div>
            <p className="text-yellow-900 dark:text-yellow-100 font-semibold">This thread is locked</p>
            <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-1">No more replies can be posted.</p>
          </div>
        )}

        {/* Child Threads Section */}
        {thread.isSubthread && (
          <div className="mt-12 border-t-2 border-slate-200 dark:border-slate-700 pt-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                Threads in this Subthread ({childThreads.length})
              </h2>
              {!showCreateThreadForm && (
                <Button onClick={() => setShowCreateThreadForm(true)} className="shadow-md">
                  + New Thread
                </Button>
              )}
            </div>

            {showCreateThreadForm && (
              <div className="mb-6">
                <CreateThreadInSubthreadForm
                  topicId={thread.topicId!}
                  parentSubthreadId={thread.id}
                  onSuccess={() => {
                    setShowCreateThreadForm(false);
                    loadThread();
                  }}
                  onCancel={() => setShowCreateThreadForm(false)}
                />
              </div>
            )}

            {childThreads.length > 0 ? (
              <div className="space-y-3">
                {childThreads.map((childThread) => (
                  <div
                    key={childThread.id}
                    onClick={() => navigate(`/forum/thread/${childThread.id}`)}
                    className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 hover:border-brand-500 dark:hover:border-brand-500 hover:shadow-md cursor-pointer transition-all duration-200"
                  >
                    <div className="flex items-start gap-4">
                      <MessageSquare className="w-6 h-6 text-brand-600 dark:text-brand-400 flex-shrink-0 mt-1" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-slate-900 dark:text-slate-100 hover:text-brand-600 dark:hover:text-brand-400 mb-1">
                          {childThread.title}
                        </h3>
                        <div className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-400">
                          <span>by {childThread.authorAlias}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(childThread.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center bg-slate-100 dark:bg-slate-800/50 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl py-12">
                <MessageSquare className="w-12 h-12 text-slate-400 dark:text-slate-600 mx-auto mb-3" />
                <p className="text-slate-600 dark:text-slate-400 font-medium">No threads yet</p>
                <p className="text-slate-500 dark:text-slate-500 text-sm mt-1">Be the first to create one!</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentGallery({ attachments }: { attachments?: string[] }) {
  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 flex flex-col gap-3">
      {attachments.map((src, idx) => (
        <div key={`${src}-${idx}`} className="relative group">
          <img
            src={src}
            alt={`Attachment ${idx + 1}`}
            className="w-full h-48 object-cover rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm"
            loading="lazy"
          />
          <div className="absolute inset-0 rounded-lg ring-2 ring-transparent group-hover:ring-brand-400/60 transition-all pointer-events-none" />
        </div>
      ))}
    </div>
  );
}

function PostCard({
  post,
  postNumber,
  isFirstPost,
  onReport,
  onReply,
  parentPost,
}: {
  post: Post;
  postNumber: number;
  isFirstPost: boolean;
  onReport: () => void;
  onReply?: () => void;
  parentPost?: Post | null;
}) {
  return (
    <div
      className={`
        bg-white dark:bg-slate-800
        border-2
        ${isFirstPost
          ? 'border-brand-200 dark:border-brand-800 shadow-lg'
          : 'border-slate-200 dark:border-slate-700 shadow-sm'
        }
        rounded-xl
        p-5 sm:p-6
        hover:shadow-md
        transition-all duration-200
        ${isFirstPost ? 'ring-2 ring-brand-100 dark:ring-brand-900/30' : ''}
      `}
    >
      <div className="flex gap-4 sm:gap-6">
        {/* Author Avatar - Enhanced */}
        <div className="flex-shrink-0">
          <div className={`
            w-14 h-14 sm:w-16 sm:h-16
            ${isFirstPost
              ? 'bg-gradient-to-br from-brand-500 to-brand-600 border-2 border-brand-400'
              : 'bg-gradient-to-br from-slate-300 to-slate-400 dark:from-slate-600 dark:to-slate-700 border-2 border-slate-400 dark:border-slate-500'
            }
            rounded-full
            flex items-center justify-center
            text-white font-bold text-xl
            shadow-md
          `}>
            {post.authorAlias.charAt(0).toUpperCase()}
          </div>
          <div className="mt-2 text-center">
            <div className="text-xs font-mono text-slate-500 dark:text-slate-500">
              #{postNumber}
            </div>
          </div>
        </div>

        {/* Post Content - Enhanced */}
        <div className="flex-1 min-w-0">
          {/* Header: Author and Timestamp - Enhanced Typography */}
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <span className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100">
              {post.authorAlias}
            </span>
            {isFirstPost && (
              <span className="px-3 py-1 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/40 dark:to-emerald-900/40 border-2 border-green-300 dark:border-green-700 rounded-full text-xs font-bold text-green-800 dark:text-green-300 shadow-sm">
                ✨ Original Poster
              </span>
            )}
            <span className="text-xs text-slate-500 dark:text-slate-500 ml-auto">
              {new Date(post.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>

          {/* Reply Context - Enhanced */}
          {post.parentPostId && parentPost && (
            <div className="mb-4 ml-0 sm:ml-4 pl-4 border-l-4 border-blue-400 dark:border-blue-600 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-r-lg p-3 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <CornerDownRight className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-xs font-bold text-blue-700 dark:text-blue-300">Replying to</span>
                <span className="text-sm font-bold text-blue-900 dark:text-blue-200">@{parentPost.authorAlias}</span>
              </div>
              <p className="text-sm text-slate-700 dark:text-slate-300 line-clamp-2 italic">
                "{parentPost.body}"
              </p>
            </div>
          )}

          {/* Post Body - Enhanced Typography */}
          <div className={`
            text-base leading-relaxed
            ${isFirstPost
              ? 'text-slate-900 dark:text-slate-100 font-medium text-lg'
              : 'text-slate-700 dark:text-slate-300'
            }
            whitespace-pre-wrap break-words mb-4
          `}>
            {post.body}
          </div>

          <AttachmentGallery attachments={post.attachments} />

          {/* Action Buttons - Redesigned */}
          <div className="flex items-center gap-3 pt-3 border-t border-slate-200 dark:border-slate-700">
            {onReply && !post.parentPostId && (
              <button
                onClick={onReply}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 hover:border-blue-300 dark:hover:border-blue-700 transition-all duration-200 shadow-sm hover:shadow"
                title="Reply to this post"
              >
                <CornerDownRight className="w-4 h-4" />
                <span>Reply</span>
              </button>
            )}
            <button
              onClick={onReport}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 hover:border-red-300 dark:hover:border-red-700 transition-all duration-200 shadow-sm hover:shadow ml-auto"
              title="Report this post"
            >
              <Flag className="w-4 h-4" />
              <span className="hidden sm:inline">Report</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReplyForm({
  threadId,
  parentPostId,
  parentPost,
  onSuccess,
  onCancel,
}: {
  threadId: string;
  parentPostId?: string;
  parentPost?: { id: string; authorAlias: string; body: string };
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [alias, setAlias] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingAlias, setLoadingAlias] = useState(true);
  const [hasAlias, setHasAlias] = useState(false);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);

  useEffect(() => {
    async function loadUserAlias() {
      try {
        setLoadingAlias(true);
        const aliases = await getCurrentAliases();

        if (aliases && aliases.length > 0) {
          const userAlias = aliases[0];
          if (userAlias && userAlias.alias) {
            setAlias(userAlias.alias);
            setHasAlias(true);
          }
        }
      } catch (err) {
        console.error('[ReplyForm] Failed to load alias:', err);
      } finally {
        setLoadingAlias(false);
      }
    }

    loadUserAlias();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!alias.trim() || !body.trim()) {
      setError('Alias and message are required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await createPost(threadId, {
        body: body.trim(),
        parentPostId: parentPostId,
        attachments,
        attachmentsFiles: attachmentFiles,
      });

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  }

  if (loadingAlias) {
    return (
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-8 text-center shadow-md">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 dark:border-brand-400"></div>
        <p className="mt-3 text-slate-600 dark:text-slate-400">Loading...</p>
      </div>
    );
  }

  if (!hasAlias) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-800 rounded-xl p-6 shadow-md">
        <h3 className="text-lg font-bold text-yellow-900 dark:text-yellow-100 mb-3 flex items-center gap-2">
          <span className="text-2xl">⚠️</span>
          Forum Alias Required
        </h3>
        <p className="text-yellow-800 dark:text-yellow-200 mb-4 leading-relaxed">
          You need to set up your forum alias before you can post replies. Your alias is your identity in the forum and will be displayed with your posts.
        </p>
        <div className="flex gap-3">
          <Link to="/profile">
            <Button className="shadow-md">Set Up Alias in Profile</Button>
          </Link>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-300 font-medium">{error}</p>
        </div>
      )}

      {parentPost && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <CornerDownRight className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-xs font-bold text-blue-700 dark:text-blue-300">Replying to</span>
            <span className="text-sm font-bold text-brand-600 dark:text-brand-400">@{parentPost.authorAlias}</span>
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-300 line-clamp-3 italic">
            "{parentPost.body}"
          </p>
        </div>
      )}

      <div>
        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Posting As</label>
        <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-900 border-2 border-slate-300 dark:border-slate-700 rounded-lg px-4 py-3">
          <span className="text-brand-600 dark:text-brand-400 font-bold text-lg">@{alias}</span>
          <Link to="/profile" className="ml-auto text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 underline">
            Change in Profile
          </Link>
        </div>
      </div>

      <div>
        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Message *</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="w-full bg-white dark:bg-slate-900 border-2 border-slate-300 dark:border-slate-700 rounded-lg px-4 py-3 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-base leading-relaxed"
          rows={8}
          placeholder="Write your reply..."
          required
        />
      </div>

      <div>
        <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Attachments (optional)</label>
        <ImageUpload
          onUpload={(files) => setAttachmentFiles((prev) => [...prev, ...files])}
          onChange={setAttachments}
          maxFiles={5}
          accept="image/*"
        />
        <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">Add up to 5 images to support your reply.</p>
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" disabled={submitting} className="flex-1 py-3 text-base font-semibold shadow-md">
          {submitting ? 'Posting...' : '✉️ Post Reply'}
        </Button>
        <Button type="button" onClick={onCancel} variant="secondary" className="px-6">
          Cancel
        </Button>
      </div>
    </form>
  );
}

function CreateThreadInSubthreadForm({
  topicId,
  parentSubthreadId,
  onSuccess,
  onCancel,
}: {
  topicId: string;
  parentSubthreadId: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [alias, setAlias] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !alias.trim() || !body.trim()) {
      setError('All fields are required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await createThread(topicId, {
        title: title.trim(),
        body: body.trim(),
        attachments,
        attachmentsFiles: attachmentFiles,
        parentSubthreadId: parentSubthreadId,
      });

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create thread');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 rounded-xl p-6 border-2 border-slate-200 dark:border-slate-700 shadow-lg">
      <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-5">Create New Thread</h3>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg p-4 mb-5">
          <p className="text-red-700 dark:text-red-300 font-medium">{error}</p>
        </div>
      )}

      <div className="space-y-5">
        <div>
          <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Thread Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full bg-white dark:bg-slate-900 border-2 border-slate-300 dark:border-slate-700 rounded-lg px-4 py-3 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-base"
            placeholder="What's your thread about?"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Your Alias *</label>
          <input
            type="text"
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            className="w-full bg-white dark:bg-slate-900 border-2 border-slate-300 dark:border-slate-700 rounded-lg px-4 py-3 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-base"
            placeholder="Anonymous name"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Initial Post *</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="w-full bg-white dark:bg-slate-900 border-2 border-slate-300 dark:border-slate-700 rounded-lg px-4 py-3 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-base leading-relaxed"
            rows={8}
            placeholder="Start the discussion..."
            required
          />
        </div>

        <div>
          <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Attachments (optional)</label>
          <ImageUpload
            onUpload={(files) => setAttachmentFiles((prev) => [...prev, ...files])}
            onChange={setAttachments}
            maxFiles={5}
            accept="image/*"
          />
          <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">Upload images to appear with your first post.</p>
        </div>

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={submitting} className="flex-1 py-3 text-base font-semibold shadow-md">
            {submitting ? 'Creating...' : 'Create Thread'}
          </Button>
          <Button type="button" onClick={onCancel} variant="secondary" className="px-6">
            Cancel
          </Button>
        </div>
      </div>
    </form>
  );
}

function ReportModal({
  targetType,
  targetId,
  onClose,
}: {
  targetType: 'thread' | 'post';
  targetId: string;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const currentUser = getStoredUserInfo();
  const reporterAlias = currentUser?.username || 'anonymous-user';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }

    if (!currentUser) {
      setError('You must be logged in to report content');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const reportPayload = {
        reporterAlias: reporterAlias,
        reason: reason.trim(),
      };

      if (targetType === 'thread') {
        await createThreadReport(targetId, reportPayload);
      } else {
        await createPostReport(targetId, reportPayload);
      }

      setSuccess(true);

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700 rounded-xl max-w-lg w-full p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Flag className="w-6 h-6 text-red-600 dark:text-red-400" />
            Report {targetType === 'thread' ? 'Thread' : 'Post'}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {success ? (
          <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 rounded-xl p-6 text-center">
            <div className="text-4xl mb-3">✓</div>
            <p className="text-green-900 dark:text-green-100 font-bold text-lg mb-2">Report Submitted Successfully</p>
            <p className="text-green-700 dark:text-green-300">
              Thank you for helping keep our community safe. Moderators will review this report.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg p-4 mb-5">
                <p className="text-red-700 dark:text-red-300 font-medium">{error}</p>
              </div>
            )}

            <div className="mb-5 p-4 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Reporting as: <span className="text-slate-900 dark:text-slate-200 font-bold">{reporterAlias}</span>
              </p>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                  Reason for Report *
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full bg-white dark:bg-slate-900 border-2 border-slate-300 dark:border-slate-700 rounded-lg px-4 py-3 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-base"
                  rows={5}
                  placeholder="Please describe why you're reporting this content..."
                  required
                  disabled={submitting}
                />
                <p className="text-xs text-slate-500 dark:text-slate-500 mt-2">
                  Examples: Spam, Harassment, Offensive content, Misinformation
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-red-600 hover:bg-red-700 py-3 text-base font-semibold shadow-md"
                >
                  {submitting ? 'Submitting...' : 'Submit Report'}
                </Button>
                <Button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  variant="secondary"
                  className="px-6"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
